import React, { useState, useEffect } from 'react';
import {
    Table, Button, Space, message, Modal, Card, Descriptions, Divider,
    Form, Input, InputNumber, Select, DatePicker, Popconfirm, Typography, Row, Col, Alert, Checkbox, Tag, Tooltip
} from 'antd';
import {
    EyeOutlined, CheckCircleOutlined, CloseCircleOutlined,
    PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, RollbackOutlined
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { apiRequest } from '../../../shared/utils/api';
import { formatMoney, PLATFORM_CURRENCY } from '../../../shared/utils/money';
import invoiceService from '../../services/invoiceService';
import apiClient from '../../../shared/services/apiClient';
import UserSelect from '../../components/UserSelect';
import InvoiceStatusTag from '../../../shared/components/InvoiceStatusTag';
import moment from 'moment';
import dayjs from 'dayjs';


/** 已付 / 已取消 / 作废 / 已退款的账单不能编辑、改项目或重新计算（后端同样拒绝）。 */
const invoiceEditable = (inv) => !!inv && !['paid', 'cancelled', 'void', 'refunded'].includes(inv.status);
const { Text } = Typography;
const { TextArea } = Input;

const Invoices = () => {
    const [searchParams] = useSearchParams();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [itemModalVisible, setItemModalVisible] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [products, setProducts] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [createForm] = Form.useForm();
    const [editForm] = Form.useForm();
    const [itemForm] = Form.useForm();
    const [refundModalVisible, setRefundModalVisible] = useState(false);
    const [refundInvoice, setRefundInvoice] = useState(null);
    const [refundForm] = Form.useForm();
    // 服务端分页 + 筛选（默认 20/页，最大 100；默认隐藏 status=cancelled 的续费单，除非显式筛选）
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);
    const [failedSettlements, setFailedSettlements] = useState([]);
    const [payInvoice, setPayInvoice] = useState(null);
    const [payForm] = Form.useForm();
    const [processing, setProcessing] = useState(null);
    const [procReason, setProcReason] = useState('');
    const [filters, setFilters] = useState(() => ({
        status: undefined,
        currency: undefined,
        invoice_type: undefined,
        user: '',
        keyword: (searchParams.get('search') || '').trim(),
        dateRange: null,
        include_cancelled_renewals: false,
    }));
    const [userInput, setUserInput] = useState('');
    const [keywordInput, setKeywordInput] = useState(() => searchParams.get('search') || '');

    const loadFailedSettlements = async () => {
        try {
            const r = await apiClient.get('/api/admin/billing/settlements', { params: { status: 'failed', page_size: 20 } });
            if (r.data?.success) setFailedSettlements(r.data.data.items || []);
        } catch (_) { /* ignore */ }
    };

    useEffect(() => { loadFailedSettlements(); }, []);

    useEffect(() => {
        loadProducts();
    }, []);

    useEffect(() => {
        loadInvoices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, filters]);

    const updateFilter = (patch) => {
        setPage(1);
        setFilters((prev) => ({ ...prev, ...patch }));
    };

    const loadInvoices = async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const params = { page, page_size: pageSize };
            if (filters.status) params.status = filters.status;
            if (filters.currency) params.currency = filters.currency;
            if (filters.invoice_type) params.invoice_type = filters.invoice_type;
            if (filters.user) params.user = filters.user;
            if (filters.keyword) params.keyword = filters.keyword;
            if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
                params.start_date = filters.dateRange[0].format('YYYY-MM-DD');
                params.end_date = filters.dateRange[1].format('YYYY-MM-DD');
            }
            if (filters.include_cancelled_renewals) params.include_cancelled_renewals = 1;
            const response = await apiRequest('GET', '/api/admin/invoices', params);
            if (response.success) {
                const list = Array.isArray(response.data) ? response.data : [];
                setInvoices(list);
                setTotal(response.pagination?.total ?? list.length);
            } else {
                setLoadError(response.error || '加载账单列表失败');
            }
        } catch (error) {
            setLoadError('加载失败：' + (error.error || error.message));
        } finally {
            setLoading(false);
        }
    };

    const loadProducts = async () => {
        try {
            const response = await invoiceService.getProducts();
            if (response.success) {
                setProducts(response.data || []);
            }
        } catch (error) {
            console.error('加载产品列表失败:', error);
        }
    };

    const handleViewDetail = async (record) => {
        try {
            const response = await apiRequest('GET', `/api/admin/invoices/get?id=${record.id}`);
            if (response.success) {
                setSelectedInvoice(response.data);
                setProcessing(null);
                setProcReason('');
                if (response.data?.status === 'paid') loadProcessing(response.data.id);
                setDetailModalVisible(true);
            } else {
                message.error(response.error || '加载账单详情失败');
            }
        } catch (error) {
            message.error('加载失败：' + error.message);
        }
    };

    // 外部链接 ?id=<invoiceId> 直接打开详情
    const linkedInvoiceId = searchParams.get('id');
    useEffect(() => {
        if (linkedInvoiceId) handleViewDetail({ id: linkedInvoiceId });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkedInvoiceId]);

    const refreshInvoiceDetail = async () => {
        if (!selectedInvoice) return;
        try {
            const response = await apiRequest('GET', `/api/admin/invoices/get?id=${selectedInvoice.id}`);
            if (response.success) {
                setSelectedInvoice(response.data);
            }
        } catch (error) {
            console.error('刷新详情失败:', error);
        }
    };

    // 线下收款登记：与真实付款同一路径（落账 → 交付 → 队列开通），重复提交幂等
    const handleMarkPaid = (record) => {
        setPayInvoice(record);
        payForm.setFieldsValue({ method: 'bank_transfer', reference: '', note: '' });
    };

    const submitMarkPaid = async (values) => {
        setSubmitting(true);
        try {
            const r = await apiRequest('POST', '/api/admin/invoices/mark-paid', { id: payInvoice.id, ...values });
            if (r.success) {
                const st = r.settlement?.status;
                message.success(r.already ? '该账单已是已支付' : (st === 'done' ? '已登记收款，开通已处理' : '已登记收款，开通处理中'));
                setPayInvoice(null);
                loadInvoices();
                loadFailedSettlements();
            } else {
                message.error(r.error || '登记失败');
            }
        } catch (error) {
            message.error(error.error || ('登记失败：' + error.message));
        } finally {
            setSubmitting(false);
        }
    };

    const loadProcessing = async (id) => {
        try {
            const r = await apiRequest('GET', '/api/admin/invoices/processing', { id });
            setProcessing(r.success ? r.data : null);
        } catch (_) {
            setProcessing(null);
        }
    };

    const processingAction = async (action) => {
        if (action === 'done' && !procReason.trim()) {
            message.warning('请填写原因');
            return;
        }
        setSubmitting(true);
        try {
            const r = await apiRequest('POST', `/api/admin/invoices/processing/${action}`, { id: selectedInvoice.id, reason: procReason });
            if (r.success) {
                message.success(action === 'retry' ? '已重新处理' : '已标记为已处理');
                setProcReason('');
            } else {
                message.error(r.error || '操作失败');
            }
            loadProcessing(selectedInvoice.id);
            loadFailedSettlements();
        } catch (error) {
            message.error(error.error || ('操作失败：' + error.message));
            loadProcessing(selectedInvoice.id);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = async (record) => {
        try {
            const response = await apiRequest('POST', '/api/admin/invoices/cancel', { id: record.id });
            if (response.success) {
                message.success('取消成功');
                loadInvoices();
            } else {
                message.error(response.error || '取消失败');
            }
        } catch (error) {
            message.error('取消失败：' + (error.error || error.message));
        }
    };

    const handleDelete = async (record) => {
        try {
            const response = await invoiceService.deleteInvoice(record.id);
            if (response.success) {
                message.success('删除成功');
                loadInvoices();
            } else {
                message.error(response.error || '删除失败');
            }
        } catch (error) {
            message.error('删除失败：' + (error.error || error.message));
        }
    };

    // 创建发票
    const handleCreateInvoice = async (values) => {
        setSubmitting(true);
        try {
            const data = {
                ...values,
                issue_date: values.issue_date ? values.issue_date.format('YYYY-MM-DD') : undefined,
                due_date: values.due_date ? values.due_date.format('YYYY-MM-DD') : undefined,
            };
            const response = await invoiceService.createInvoice(data);
            if (response.success) {
                message.success('发票创建成功');
                setCreateModalVisible(false);
                createForm.resetFields();
                loadInvoices();
                // 自动打开详情进行项目添加
                handleViewDetail(response.data);
            } else {
                message.error(response.error || '创建失败');
            }
        } catch (error) {
            message.error('创建失败：' + (error.error || error.message));
        } finally {
            setSubmitting(false);
        }
    };

    // 编辑发票
    const handleOpenEditModal = () => {
        if (selectedInvoice) {
            editForm.setFieldsValue({
                ...selectedInvoice,
                due_date: selectedInvoice.due_date ? dayjs(selectedInvoice.due_date) : null,
            });
            setEditModalVisible(true);
        }
    };

    const handleEditInvoice = async (values) => {
        setSubmitting(true);
        try {
            const data = {
                ...values,
                due_date: values.due_date ? values.due_date.format('YYYY-MM-DD') : undefined,
            };
            const response = await invoiceService.updateInvoice(selectedInvoice.id, data);
            if (response.success) {
                message.success('发票更新成功');
                setEditModalVisible(false);
                refreshInvoiceDetail();
                loadInvoices();
            } else {
                message.error(response.error || '更新失败');
            }
        } catch (error) {
            message.error('更新失败：' + (error.error || error.message));
        } finally {
            setSubmitting(false);
        }
    };

    // 添加/编辑发票项
    const handleOpenItemModal = (item = null) => {
        setEditingItem(item);
        if (item) {
            itemForm.setFieldsValue(item);
        } else {
            itemForm.resetFields();
            itemForm.setFieldsValue({
                quantity: 1,
                unit_price: 0,
                item_type: 'custom',
            });
        }
        setItemModalVisible(true);
    };

    const handleSaveItem = async (values) => {
        setSubmitting(true);
        try {
            let response;
            if (editingItem) {
                response = await invoiceService.updateInvoiceItem(editingItem.id, values);
            } else {
                response = await invoiceService.addInvoiceItem({
                    ...values,
                    invoice_id: selectedInvoice.id,
                });
            }
            if (response.success) {
                message.success(editingItem ? '更新成功' : '添加成功');
                setItemModalVisible(false);
                refreshInvoiceDetail();
                loadInvoices();
            } else {
                message.error(response.error || '操作失败');
            }
        } catch (error) {
            message.error('操作失败：' + (error.error || error.message));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteItem = async (itemId) => {
        try {
            const response = await invoiceService.deleteInvoiceItem(itemId);
            if (response.success) {
                message.success('删除成功');
                refreshInvoiceDetail();
                loadInvoices();
            } else {
                message.error(response.error || '删除失败');
            }
        } catch (error) {
            message.error('删除失败：' + (error.error || error.message));
        }
    };

    // 退款
    const getRefundableAmount = (record) => {
        const paid = parseFloat(record?.paid_amount) || 0;
        if (paid > 0) return paid;
        return parseFloat(record?.total_amount) || 0;
    };

    // 本地货币扣款（EUR/GBP…）：账本按 USD 退，Stripe 按扣款币种原路退（按付款时汇率快照折算）
    const chargeEquivalent = (record, amount) => {
        const g = record?.gateway_charge;
        if (!g || !g.charged_currency || !(g.amount > 0)) return null;
        return formatMoney((Number(amount) || 0) * g.charged_amount / g.amount, g.charged_currency);
    };

    const handleOpenRefund = (record) => {
        setRefundInvoice(record);
        refundForm.setFieldsValue({
            amount: getRefundableAmount(record),
            target: 'auto',
        });
        setRefundModalVisible(true);
    };

    const handleRefund = async (values) => {
        const refundable = getRefundableAmount(refundInvoice);
        const isFull = values.amount >= refundable - 0.005;
        Modal.confirm({
            title: '确认退款',
            okType: 'danger',
            okText: '确认退款',
            content: (
                <div>
                    <p>即将对账单 <b>{refundInvoice.invoice_number}</b> 退款 <b>{formatMoney(values.amount, refundInvoice.currency || PLATFORM_CURRENCY)}</b>{isFull ? '（全额）' : '（部分）'}。</p>
                    {values.target !== 'balance' && chargeEquivalent(refundInvoice, values.amount) && (
                        <p>原路退回到卡/银行：约 <b>{chargeEquivalent(refundInvoice, values.amount)}</b>（按付款时汇率，Stripe 按扣款币种退款）</p>
                    )}
                    <p>退款去向：{values.target === 'balance' ? '退到用户余额' : '自动（有 Stripe 支付则原路退卡，否则退到余额）'}</p>
                    {refundInvoice.invoice_type === 'topup' && (
                        <p style={{ color: '#fa541c' }}>该账单为充值账单，退款将从用户余额中扣回对应金额；余额不足（已消费）时将被拒绝。</p>
                    )}
                    <p style={{ color: '#fa541c' }}>此操作涉及真实资金流动，不可撤销，请再次确认！</p>
                </div>
            ),
            onOk: async () => {
                setSubmitting(true);
                try {
                    const response = await invoiceService.refundInvoice({
                        invoice_id: refundInvoice.id,
                        amount: values.amount,
                        target: values.target,
                    });
                    if (response.success) {
                        message.success(response.message || '退款成功');
                        setRefundModalVisible(false);
                        refundForm.resetFields();
                        loadInvoices();
                    } else {
                        message.error(response.error || '退款失败');
                    }
                } catch (error) {
                    message.error('退款失败：' + (error.error || error.message));
                } finally {
                    setSubmitting(false);
                }
            },
        });
    };

    const handleRecalculate = async () => {
        try {
            const response = await invoiceService.recalculateInvoice(selectedInvoice.id);
            if (response.success) {
                message.success('重新计算成功');
                refreshInvoiceDetail();
                loadInvoices();
            } else {
                message.error(response.error || '计算失败');
            }
        } catch (error) {
            message.error('计算失败：' + (error.error || error.message));
        }
    };

    const getStatusTag = (status) => <InvoiceStatusTag status={status} />;

    const columns = [
        {
            title: '账单号',
            dataIndex: 'invoice_number',
            key: 'invoice_number',
            width: 170,
            ellipsis: { showTitle: false },
            render: (v, r) => (
                <Tooltip title={v}>
                    <a onClick={() => handleViewDetail(r)}>{v}</a>
                </Tooltip>
            ),
        },
        {
            title: '用户',
            dataIndex: 'username',
            key: 'username',
            width: 120,
            ellipsis: { showTitle: false },
            render: (v, r) => <Tooltip title={r.email || v}>{v || `#${r.user_id}`}</Tooltip>,
        },
        {
            title: '类型',
            dataIndex: 'invoice_type',
            key: 'invoice_type',
            width: 90,
            render: (type) => {
                const typeMap = {
                    subscription: '订阅',
                    renewal: '续费',
                    upgrade: '升级',
                    addon: '附加',
                    usage: '超额用量',
                    topup: '充值',
                    domain: '域名',
                    domain_registration: '域名注册',
                    domain_renewal: '域名续费',
                    domain_transfer: '域名转入',
                    ssl: 'SSL',
                    adjustment: '调整',
                    manual: '手动',
                };
                return typeMap[type] || type;
            }
        },
        {
            title: '总金额',
            dataIndex: 'total_amount',
            key: 'total_amount',
            width: 100,
            render: (amount, r) => formatMoney(amount, r.currency || PLATFORM_CURRENCY),
        },
        {
            title: '已支付',
            dataIndex: 'paid_amount',
            key: 'paid_amount',
            width: 100,
            render: (amount, r) => formatMoney(amount, r.currency || PLATFORM_CURRENCY),
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 90,
            render: (status) => getStatusTag(status),
        },
        {
            title: '到期日期',
            dataIndex: 'due_date',
            key: 'due_date',
            width: 105,
            render: (date) => date ? moment(date).format('YYYY-MM-DD') : '-',
        },
        {
            title: '创建时间',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 105,
            render: (date) => date ? (
                <Tooltip title={moment(date).format('YYYY-MM-DD HH:mm:ss')}>{moment(date).format('YYYY-MM-DD')}</Tooltip>
            ) : '-',
        },
        {
            title: '操作',
            key: 'action',
            width: 210,
            fixed: 'right',
            render: (_, record) => (
                <Space size={0}>
                    <Tooltip title="详情">
                        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)} />
                    </Tooltip>
                    {(record.status === 'unpaid' || record.status === 'partial') && (
                        <Button
                            type="link"
                            size="small"
                            icon={<CheckCircleOutlined />}
                            onClick={() => handleMarkPaid(record)}
                        >
                            登记收款
                        </Button>
                    )}
                    {record.status === 'paid' && (
                        <Button
                            type="link"
                            size="small"
                            danger
                            icon={<RollbackOutlined />}
                            onClick={() => handleOpenRefund(record)}
                        >
                            退款
                        </Button>
                    )}
                    {record.status === 'unpaid' && (
                        <>
                            <Popconfirm
                                title={`取消账单 ${record.invoice_number}？`}
                                okText="取消账单" okButtonProps={{ danger: true }} cancelText="返回"
                                onConfirm={() => handleCancel(record)}
                            >
                                <Tooltip title="取消">
                                    <Button type="link" size="small" danger icon={<CloseCircleOutlined />} />
                                </Tooltip>
                            </Popconfirm>
                            <Popconfirm
                                title={`删除账单 ${record.invoice_number}？`}
                                okText="删除" okButtonProps={{ danger: true }} cancelText="返回"
                                onConfirm={() => handleDelete(record)}
                            >
                                <Tooltip title="删除">
                                    <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                                </Tooltip>
                            </Popconfirm>
                        </>
                    )}
                </Space>
            ),
        },
    ];

    const itemColumns = [
        {
            title: '项目类型',
            dataIndex: 'item_type',
            key: 'item_type',
            width: 120,
            render: (type) => {
                const typeMap = {
                    subscription: '订阅',
                    subscription_upgrade: '订阅升级',
                    renewal: '续费',
                    addon: '附加包',
                    usage_overage: '超额用量',
                    domain_registration: '域名注册',
                    domain_renewal: '域名续费',
                    domain_transfer: '域名转入',
                    ssl: 'SSL',
                    topup: '充值',
                    evm_addon: 'EVM 附加',
                    credit: '抵扣',
                    custom: '自定义',
                };
                return typeMap[type] || type;
            }
        },
        {
            title: '描述',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: '数量',
            dataIndex: 'quantity',
            key: 'quantity',
            width: 80,
        },
        {
            title: '单价',
            dataIndex: 'unit_price',
            key: 'unit_price',
            width: 100,
            render: (price) => formatMoney(price, selectedInvoice?.currency || PLATFORM_CURRENCY),
        },
        {
            title: '金额',
            dataIndex: 'amount',
            key: 'amount',
            width: 100,
            render: (amount) => (
                <span style={{ fontWeight: 'bold', color: amount < 0 ? '#52c41a' : '#000' }}>
                    {formatMoney(amount, selectedInvoice?.currency || PLATFORM_CURRENCY)}
                </span>
            ),
        },
        ...(invoiceEditable(selectedInvoice) ? [{
            title: '操作',
            key: 'action',
            width: 120,
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleOpenItemModal(record)}
                    />
                    <Popconfirm
                        title="确定删除此项目？"
                        onConfirm={() => handleDeleteItem(record.id)}
                    >
                        <Button
                            type="link"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </Space>
            ),
        }] : []),
    ];

    return (
        <div>
            {failedSettlements.length > 0 && (
                <Alert
                    type="error" showIcon style={{ marginBottom: 12 }}
                    message={`有 ${failedSettlements.length} 笔付款后交付失败（已收钱未交付），需要处理`}
                    description={
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            {failedSettlements.map((f) => (
                                <Space key={f.invoice_id} wrap>
                                    <Text code>{f.invoice_number}</Text>
                                    <Tag>{f.invoice_type}</Tag>
                                    <Text>{f.total_amount} {f.currency}</Text>
                                    <Text type="secondary">用户 {f.username || f.user_id} · 已试 {f.attempts} 次</Text>
                                    <Text type="danger" style={{ maxWidth: 420 }} ellipsis={{ tooltip: f.last_error }}>{f.last_error}</Text>
                                    <Button size="small" onClick={async () => {
                                        const r = await apiClient.post('/api/admin/billing/settlements/retry', { invoice_id: f.invoice_id }).then((x) => x.data).catch((e) => e.response?.data || { success: false });
                                        if (r.success) { message.success('已重试成功'); } else { message.error(r.data?.last_error || r.error || '仍然失败'); }
                                        loadFailedSettlements();
                                    }}>重试</Button>
                                </Space>
                            ))}
                        </Space>
                    }
                />
            )}
            <Card
                title="账单管理"
                extra={
                    <Space>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setCreateModalVisible(true)}
                        >
                            创建发票
                        </Button>
                        <Button icon={<ReloadOutlined />} onClick={loadInvoices} loading={loading}>
                            刷新
                        </Button>
                    </Space>
                }
            >
                <Space wrap style={{ marginBottom: 16 }}>
                    <Select
                        allowClear
                        placeholder="状态"
                        style={{ width: 120 }}
                        value={filters.status}
                        onChange={(v) => updateFilter({ status: v })}
                        options={[
                            { value: 'unpaid', label: '未支付' },
                            { value: 'partial', label: '部分支付' },
                            { value: 'paid', label: '已支付' },
                            { value: 'cancelled', label: '已取消' },
                            { value: 'refunded', label: '已退款' },
                            { value: 'void', label: '作废' },
                            { value: 'draft', label: '草稿' },
                        ]}
                    />
                    <Select
                        allowClear
                        placeholder="类型"
                        style={{ width: 140 }}
                        value={filters.invoice_type}
                        onChange={(v) => updateFilter({ invoice_type: v })}
                        options={[
                            { value: 'subscription', label: '订阅' },
                            { value: 'renewal', label: '续费' },
                            { value: 'upgrade', label: '升级' },
                            { value: 'ssl', label: 'SSL' },
                            { value: 'domain_registration', label: '域名注册' },
                            { value: 'domain_renewal', label: '域名续费' },
                            { value: 'domain_transfer', label: '域名转入' },
                            { value: 'addon', label: '附加' },
                            { value: 'usage', label: '超额用量' },
                            { value: 'topup', label: '充值' },
                            { value: 'adjustment', label: '调整' },
                        ]}
                    />
                    <Select
                        allowClear
                        placeholder="币种"
                        style={{ width: 100 }}
                        value={filters.currency}
                        onChange={(v) => updateFilter({ currency: v })}
                        options={['USD', 'CNY', 'EUR', 'HKD'].map((c) => ({ value: c, label: c }))}
                    />
                    <Input.Search
                        allowClear
                        placeholder="用户 ID / 用户名 / 邮箱"
                        style={{ width: 220 }}
                        value={userInput}
                        onChange={(e) => setUserInput(e.target.value)}
                        onSearch={(v) => updateFilter({ user: (v || '').trim() })}
                    />
                    <Input.Search
                        allowClear
                        placeholder="发票号"
                        style={{ width: 200 }}
                        value={keywordInput}
                        onChange={(e) => setKeywordInput(e.target.value)}
                        onSearch={(v) => updateFilter({ keyword: (v || '').trim() })}
                    />
                    <DatePicker.RangePicker
                        value={filters.dateRange}
                        onChange={(v) => updateFilter({ dateRange: v })}
                    />
                    <Checkbox
                        checked={filters.include_cancelled_renewals}
                        onChange={(e) => updateFilter({ include_cancelled_renewals: e.target.checked })}
                    >
                        显示已取消的续费单
                    </Checkbox>
                </Space>
                {loadError && (
                    <Alert
                        type="error" showIcon style={{ marginBottom: 12 }}
                        message={loadError}
                        action={<Button size="small" onClick={loadInvoices}>重试</Button>}
                    />
                )}
                <Table
                    columns={columns}
                    dataSource={invoices}
                    rowKey="id"
                    loading={loading}
                    sticky
                    scroll={{ x: 1100 }}
                    pagination={{
                        position: ['topRight', 'bottomRight'],
                        current: page,
                        pageSize,
                        total,
                        showSizeChanger: true,
                        pageSizeOptions: ['20', '50', '100'],
                        showTotal: (t) => `共 ${t} 条记录`,
                        onChange: (p, ps) => {
                            if (ps !== pageSize) { setPageSize(ps); setPage(1); } else { setPage(p); }
                        },
                    }}
                />
            </Card>

            {/* 创建发票模态框 */}
            <Modal
                title="创建发票"
                open={createModalVisible}
                onCancel={() => setCreateModalVisible(false)}
                onOk={() => createForm.submit()}
                confirmLoading={submitting}
                width={600}
            >
                <Form
                    form={createForm}
                    layout="vertical"
                    onFinish={handleCreateInvoice}
                    initialValues={{
                        invoice_type: 'manual',
                        currency: PLATFORM_CURRENCY,
                        tax_rate: 0,
                        discount_amount: 0,
                    }}
                >
                    <Form.Item
                        name="user_id"
                        label="用户"
                        rules={[{ required: true, message: '请选择用户' }]}
                    >
                        <UserSelect placeholder="选择用户" style={{ width: '100%' }} />
                    </Form.Item>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="invoice_type"
                                label="发票类型"
                                rules={[{ required: true }]}
                            >
                                <Select>
                                    <Select.Option value="manual">手动</Select.Option>
                                    <Select.Option value="subscription">订阅</Select.Option>
                                    <Select.Option value="renewal">续费</Select.Option>
                                    <Select.Option value="domain">域名</Select.Option>
                                    <Select.Option value="ssl">SSL</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="currency" label="货币">
                                <Select>
                                    <Select.Option value="USD">USD</Select.Option>
                                    <Select.Option value="CNY">CNY</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="tax_rate" label="税率 (%)">
                                <InputNumber min={0} max={100} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="discount_amount" label="折扣金额">
                                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="issue_date" label="开票日期">
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="due_date" label="到期日期">
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="notes" label="备注">
                        <TextArea rows={3} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 详情模态框 */}
            <Modal
                title="账单详情"
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={[
                    invoiceEditable(selectedInvoice) && (
                        <Button key="edit" icon={<EditOutlined />} onClick={handleOpenEditModal}>
                            编辑
                        </Button>
                    ),
                    invoiceEditable(selectedInvoice) && (
                        <Button key="recalc" icon={<ReloadOutlined />} onClick={handleRecalculate}>
                            重新计算
                        </Button>
                    ),
                    <Button key="close" onClick={() => setDetailModalVisible(false)}>
                        关闭
                    </Button>,
                ].filter(Boolean)}
                width={1000}
            >
                {selectedInvoice && (
                    <>
                        <Descriptions bordered column={2} size="small">
                            <Descriptions.Item label="账单号" span={2}>
                                {selectedInvoice.invoice_number}
                            </Descriptions.Item>
                            <Descriptions.Item label="用户ID">{selectedInvoice.user_id}</Descriptions.Item>
                            <Descriptions.Item label="用户名">{selectedInvoice.username || '-'}</Descriptions.Item>
                            <Descriptions.Item label="小计">{formatMoney(selectedInvoice.subtotal, selectedInvoice.currency || PLATFORM_CURRENCY)}</Descriptions.Item>
                            <Descriptions.Item label="税率">{selectedInvoice.tax_rate || 0}%</Descriptions.Item>
                            <Descriptions.Item label="税额">{formatMoney(selectedInvoice.tax_amount || 0, selectedInvoice.currency || PLATFORM_CURRENCY)}</Descriptions.Item>
                            <Descriptions.Item label="折扣">{formatMoney(selectedInvoice.discount_amount || 0, selectedInvoice.currency || PLATFORM_CURRENCY)}</Descriptions.Item>
                            <Descriptions.Item label="总金额">
                                <Text strong style={{ fontSize: 16, color: '#1890ff' }}>
                                    {formatMoney(selectedInvoice.total_amount, selectedInvoice.currency || PLATFORM_CURRENCY)}
                                </Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="已支付">
                                <Text strong style={{ fontSize: 16, color: '#52c41a' }}>
                                    {formatMoney(selectedInvoice.paid_amount, selectedInvoice.currency || PLATFORM_CURRENCY)}
                                </Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="状态">
                                {getStatusTag(selectedInvoice.status)}
                            </Descriptions.Item>
                            <Descriptions.Item label="货币">{selectedInvoice.currency || PLATFORM_CURRENCY}</Descriptions.Item>
                            <Descriptions.Item label="支付方式">
                                {selectedInvoice.payment_method || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="到期日期">
                                {selectedInvoice.due_date ? moment(selectedInvoice.due_date).format('YYYY-MM-DD') : '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="支付时间">
                                {selectedInvoice.paid_at ? moment(selectedInvoice.paid_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="创建时间">
                                {selectedInvoice.created_at ? moment(selectedInvoice.created_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="备注" span={2}>
                                {selectedInvoice.notes || '-'}
                            </Descriptions.Item>
                        </Descriptions>

                        {selectedInvoice.status === 'paid' && (
                            <>
                                <Divider orientation="left">开通处理</Divider>
                                {(() => {
                                    const st = processing?.settlement;
                                    const label = !st ? '无记录' : st.stuck ? '卡住' : ({ pending: '待处理', processing: '处理中', done: '已完成', failed: '失败' }[st.status] || st.status);
                                    const color = !st ? 'default' : st.status === 'done' ? 'green' : (st.status === 'failed' || st.stuck) ? 'red' : 'blue';
                                    const actionable = !st || st.status === 'failed' || st.status === 'pending' || st.stuck;
                                    return (
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            <Space wrap>
                                                <Tag color={color}>{label}</Tag>
                                                {st && <Text type="secondary">尝试 {st.attempts} 次</Text>}
                                                {st?.done_at && <Text type="secondary">完成于 {moment(st.done_at).format('YYYY-MM-DD HH:mm')}</Text>}
                                            </Space>
                                            {st?.last_error && <Alert type={st.status === 'done' ? 'info' : 'error'} showIcon message={st.last_error} />}
                                            {actionable && (
                                                <Space wrap>
                                                    <Button type="primary" loading={submitting} onClick={() => processingAction('retry')}>重新处理</Button>
                                                    <Input style={{ width: 260 }} placeholder="线下已处理的原因" value={procReason} onChange={(e) => setProcReason(e.target.value)} />
                                                    <Button loading={submitting} onClick={() => processingAction('done')}>标记已处理</Button>
                                                </Space>
                                            )}
                                            {processing?.logs?.length > 0 && (
                                                <Table size="small" rowKey="id" pagination={false} dataSource={processing.logs}
                                                    columns={[
                                                        { title: '时间', dataIndex: 'created_at', width: 150, render: (v) => moment(v).format('MM-DD HH:mm') },
                                                        { title: '管理员', dataIndex: 'admin_name', width: 100, render: (v, r) => v || `#${r.admin_id}` },
                                                        { title: '操作', dataIndex: 'action', width: 110, render: (v) => ({ mark_paid: '登记收款', settlement_retry: '重新处理', settlement_mark_done: '标记已处理' }[v] || v) },
                                                        { title: '状态', width: 140, render: (_, r) => `${r.from_status || '-'} → ${r.to_status || '-'}` },
                                                        { title: '原因', dataIndex: 'reason' },
                                                    ]} />
                                            )}
                                        </Space>
                                    );
                                })()}
                            </>
                        )}

                        <Divider orientation="left">
                            账单项目
                            {invoiceEditable(selectedInvoice) && (
                                <Button
                                    type="link"
                                    icon={<PlusOutlined />}
                                    onClick={() => handleOpenItemModal()}
                                    style={{ marginLeft: 16 }}
                                >
                                    添加项目
                                </Button>
                            )}
                        </Divider>
                        <Table
                            dataSource={selectedInvoice.items || []}
                            rowKey="id"
                            pagination={false}
                            columns={itemColumns}
                            size="small"
                        />
                    </>
                )}
            </Modal>

            {/* 编辑发票模态框 */}
            <Modal
                title="编辑发票"
                open={editModalVisible}
                onCancel={() => setEditModalVisible(false)}
                onOk={() => editForm.submit()}
                confirmLoading={submitting}
                width={600}
            >
                <Form
                    form={editForm}
                    layout="vertical"
                    onFinish={handleEditInvoice}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="tax_rate" label="税率 (%)">
                                <InputNumber min={0} max={100} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="discount_amount" label="折扣金额">
                                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="due_date" label="到期日期">
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="notes" label="备注">
                        <TextArea rows={3} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 退款模态框 */}
            <Modal
                title={`账单退款 - ${refundInvoice?.invoice_number || ''}`}
                open={refundModalVisible}
                onCancel={() => setRefundModalVisible(false)}
                onOk={() => refundForm.submit()}
                okText="退款"
                okButtonProps={{ danger: true }}
                confirmLoading={submitting}
                width={520}
            >
                {refundInvoice && (
                    <>
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message="退款涉及真实资金流动，操作不可撤销"
                            description={
                                refundInvoice.invoice_type === 'topup'
                                    ? '充值账单退款：将从用户余额中扣回对应金额（余额不足会被拒绝），如原支付为 Stripe 且选择"自动"，资金将原路退回。'
                                    : '全额退款后账单状态变为"已退款"；部分退款保持"已支付"并在备注中记录。"自动"优先经 Stripe 原路退回，无网关支付记录时退到用户余额。'
                            }
                        />
                        {refundInvoice.gateway_charge?.charged_currency && (
                            <Alert type="info" showIcon style={{ marginBottom: 16 }}
                                message={`该账单以 ${refundInvoice.gateway_charge.charged_currency} 付款：实扣 ${formatMoney(refundInvoice.gateway_charge.charged_amount, refundInvoice.gateway_charge.charged_currency)}，入账 ${formatMoney(refundInvoice.gateway_charge.amount, refundInvoice.gateway_charge.currency)}（汇率 ${refundInvoice.gateway_charge.fx_rate}）。原路退款按此比例折算成 ${refundInvoice.gateway_charge.charged_currency}。`} />
                        )}
                        <Form form={refundForm} layout="vertical" onFinish={handleRefund}>
                            <Form.Item
                                name="amount"
                                label={`退款金额（最多 ${formatMoney(getRefundableAmount(refundInvoice), (refundInvoice?.currency || PLATFORM_CURRENCY))}）`}
                                rules={[
                                    { required: true, message: '请输入退款金额' },
                                    {
                                        validator: (_, value) => {
                                            const max = getRefundableAmount(refundInvoice);
                                            if (value === undefined || value === null) return Promise.resolve();
                                            if (value <= 0) return Promise.reject(new Error('退款金额必须大于0'));
                                            if (value > max) return Promise.reject(new Error(`退款金额不能超过已支付金额 ${formatMoney(max, (refundInvoice?.currency || PLATFORM_CURRENCY))}`));
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <InputNumber
                                    min={0.01}
                                    max={getRefundableAmount(refundInvoice)}
                                    precision={2}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                            <Form.Item name="target" label="退款去向" rules={[{ required: true }]}>
                                <Select>
                                    <Select.Option value="auto">自动（优先 Stripe 原路退回）</Select.Option>
                                    <Select.Option value="balance">退到余额</Select.Option>
                                </Select>
                            </Form.Item>
                        </Form>
                    </>
                )}
            </Modal>

            {/* 发票项模态框 */}
            <Modal
                title={editingItem ? '编辑项目' : '添加项目'}
                open={itemModalVisible}
                onCancel={() => setItemModalVisible(false)}
                onOk={() => itemForm.submit()}
                confirmLoading={submitting}
                width={600}
            >
                <Form
                    form={itemForm}
                    layout="vertical"
                    onFinish={handleSaveItem}
                >
                    <Form.Item
                        name="description"
                        label="描述"
                        rules={[{ required: true, message: '请输入描述' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="item_type" label="项目类型">
                                <Select>
                                    <Select.Option value="custom">自定义</Select.Option>
                                    <Select.Option value="subscription">订阅</Select.Option>
                                    <Select.Option value="domain_registration">域名注册</Select.Option>
                                    <Select.Option value="domain_renewal">域名续费</Select.Option>
                                    <Select.Option value="credit">抵扣</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="product_id" label="关联产品">
                                <Select allowClear placeholder="选择产品">
                                    {products.map(p => (
                                        <Select.Option key={p.id} value={p.id}>
                                            {p.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="quantity" label="数量">
                                <InputNumber min={1} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="unit_price" label="单价">
                                <InputNumber precision={2} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="amount"
                                label="金额"
                                tooltip="留空则自动计算（数量×单价）"
                            >
                                <InputNumber precision={2} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
            {/* 登记收款 */}
            <Modal
                title={payInvoice ? `登记收款 · ${payInvoice.invoice_number}` : '登记收款'}
                open={!!payInvoice}
                onCancel={() => setPayInvoice(null)}
                onOk={() => payForm.submit()}
                confirmLoading={submitting}
                okText="确认已收款"
                destroyOnClose
            >
                {payInvoice && (
                    <Form form={payForm} layout="vertical" onFinish={submitMarkPaid}>
                        <Form.Item label="金额">
                            <Text strong>{formatMoney(payInvoice.total_amount, payInvoice.currency || PLATFORM_CURRENCY)}</Text>
                        </Form.Item>
                        <Form.Item name="method" label="收款方式" rules={[{ required: true }]}>
                            <Select options={[
                                { value: 'bank_transfer', label: '银行转账' },
                                { value: 'alipay', label: '支付宝（线下）' },
                                { value: 'wechat', label: '微信（线下）' },
                                { value: 'cash', label: '现金' },
                                { value: 'offline', label: '其他线下' },
                                { value: 'other', label: '其他' },
                            ]} />
                        </Form.Item>
                        <Form.Item name="reference" label="流水号">
                            <Input maxLength={200} placeholder="选填" />
                        </Form.Item>
                        <Form.Item name="note" label="备注" rules={[{ required: true, message: '请填写备注' }]}>
                            <TextArea rows={2} maxLength={500} />
                        </Form.Item>
                    </Form>
                )}
            </Modal>
        </div>
    );
};

export default Invoices;
